import { DocRenderer } from '@cyntler/react-doc-viewer';
import { ClickAwayListener } from '@material-ui/core';
import { throttle } from 'lodash';
import React, { useContext } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import { ClickAwayContext } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/context';
import { PreviewControls } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/PreviewControls';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const MIN_ZOOM = 1;
const MAX_ZOOM = 2;
export const PdfRenderer: DocRenderer = (props) => {
  const [activePage, setActivePage] = React.useState(1);
  const [zoom, setZoom] = React.useState(1);
  const { onClickAway } = useContext(ClickAwayContext);
  const [totalPages, setTotalPages] = React.useState<number | null>(null);
  const pagesRefs = React.useRef<Array<React.RefObject<HTMLDivElement>>>([]);

  const handlePageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let page = parseInt(e.target.value);
    // Scroll to the specified page if it exists
    if (totalPages) {
      page = Math.max(1, Math.min(page, totalPages));
    }
    setActivePage(page);
    const targetPage = pagesRefs.current[page - 1]?.current;
    if (targetPage) {
      targetPage.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      });
    }
  };
  const handleZoomIn = () => {
    if (zoom < MAX_ZOOM) {
      setZoom(zoom + 0.1);
    }
  };

  const handleZoomOut = () => {
    if (zoom > MIN_ZOOM) {
      setZoom(zoom - 0.1);
    }
  };

  const [pdfPageWidth, setPdfPageWidth] = React.useState(0);
  const pdfWrapper = React.useRef<HTMLDivElement>(null);

  /**
   * In order to keep the pdf page width responsive we need to set the width of the pdf page
   * to the width of the parent container. This function handles the pdf page width resizing.
   */
  const setPdfSize = () => {
    if (pdfWrapper && pdfWrapper.current) {
      setPdfPageWidth(pdfWrapper.current.getBoundingClientRect().width);
    }
  };

  React.useEffect(() => {
    window.addEventListener('resize', throttle(setPdfSize, 3000));
    setPdfSize();
    return () => {
      window.removeEventListener('resize', throttle(setPdfSize, 3000));
    };
  }, []);

  const observerCallback = React.useCallback(
    (entries: Array<IntersectionObserverEntry>) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const pageIndex = Number((entry.target as HTMLElement).dataset.index);
          setActivePage(pageIndex + 1); // Pages are 1-based
        }
      });
    },
    [],
  );

  React.useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    });

    pagesRefs.current.reverse().forEach((ref) => {
      if (ref && ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [totalPages, observerCallback]);

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div
        className="flex justify-center h-full pt-16 md:w-1/2 mx-auto absolute inset-0"
        ref={pdfWrapper}
      >
        {totalPages && (
          <PreviewControls
            pages={totalPages}
            activePage={activePage}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            resetZoom={() => setZoom(1)}
            onPageChange={handlePageChange}
          />
        )}
        <Document
          file={props.mainState.currentDocument?.fileData}
          onLoadSuccess={({ numPages }: { numPages: number }) => {
            setTotalPages(numPages);
            // init pages refs
            pagesRefs.current = Array(numPages)
              .fill(null)
              .map(() => React.createRef());
          }}
        >
          {Array.from(new Array(totalPages || 0), (_1, index) => {
            return (
              <div
                key={index}
                ref={pagesRefs.current[index]}
                data-index={index}
                className="mb-4"
              >
                <Page
                  pageNumber={index + 1}
                  scale={zoom}
                  width={pdfPageWidth}
                />
              </div>
            );
          })}
        </Document>
      </div>
    </ClickAwayListener>
  );
};

PdfRenderer.fileTypes = ['pdf', 'application/pdf'];
PdfRenderer.weight = 1;
